import { createContext, useContext, useCallback, useState, useMemo } from 'react';

/**
 * NotificationContext
 * Global toast notification system
 * Manages a queue of toast notifications with auto-dismiss and manual control
 */

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  /**
   * Show a new toast notification
   * @param {Object} options - Toast configuration
   * @param {string} options.type - 'success'|'error'|'info'|'warning'
   * @param {string} options.message - Toast message (required)
   * @param {string} options.title - Toast title (optional)
   * @param {string} options.description - Alternative to message (optional)
   * @param {number} options.duration - Auto-dismiss duration in ms (default 8000, 0 for no auto-dismiss)
   * @param {number} options.durationMs - Alternative to duration (optional)
   * @returns {string} Toast ID
   */
  const showToast = useCallback((options) => {
    const { type = 'info', message, title, description, duration, durationMs } = options;

    const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

    // Pass through all options to the toast component
    // The ToastContainer will handle the auto-dismiss timer
    setToasts((prev) => [
      ...prev,
      {
        id,
        type,
        message: message || description,
        title,
        description: description || message,
        duration,
        durationMs
      }
    ]);

    return id;
  }, []);

  /**
   * Dismiss a specific toast by ID
   * @param {string} id - Toast ID to dismiss
   */
  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /**
   * Clear all toasts immediately
   */
  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const value = useMemo(
    () => ({
      toasts,
      showToast,
      dismissToast,
      clearAll,
      // Legacy API compatibility
      addToast: showToast,
      removeToast: dismissToast,
      clear: clearAll
    }),
    [toasts, showToast, dismissToast, clearAll]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
